// @spec IAM-STR-012
// TanStack Query hook for fetching user's organizations

import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { fetchOrganizations, type UserOrganization } from '@/api/organizations';
import { createApiClient } from '@/lib/api-client';

/**
 * Query key for organizations data.
 *
 * Kept as a bare key for backward compatibility with `invalidateQueries`
 * callers. The query itself is keyed by the active Clerk org id so data
 * never bleeds across a switch.
 */
export const organizationsQueryKey = ['organizations'] as const;

/**
 * Stale time for organizations query (30 seconds).
 *
 * Membership data can change as users create subsidiaries, reparent, or
 * accept invitations. A short stale window keeps the list responsive
 * without hammering the API — the query is cheap.
 */
const ORGANIZATIONS_STALE_TIME = 30 * 1000;

/**
 * Return type for the useOrganizations hook.
 */
export interface UseOrganizationsReturn {
  organizations: UserOrganization[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  /** True when backend returns no orgs but Clerk has an active organization (sync issue). */
  hasClerkOrgMismatch: boolean;
}

/**
 * Hook to fetch the authenticated user's organizations.
 *
 * Uses TanStack Query with a 5-minute stale time since
 * organization memberships don't change frequently.
 *
 * Also detects a mismatch between Clerk's active organization
 * and the backend's organization list, which indicates a sync issue.
 *
 * @example
 * function OrganizationsList() {
 *   const { organizations, isLoading, error, hasClerkOrgMismatch } = useOrganizations();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (hasClerkOrgMismatch) return <SyncIssueMessage />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return organizations.map(org => <OrgCard key={org.id} org={org} />);
 * }
 */
export function useOrganizations(): UseOrganizationsReturn {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { organization: clerkOrg } = useOrganization();

  const query = useQuery({
    // Namespace by the active Clerk org so that switching context spawns a
    // fresh cache entry rather than re-reading a stale response computed
    // against the previous JWT ``org_id`` claim.
    queryKey: [...organizationsQueryKey, clerkOrg?.id ?? null],
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });
      return fetchOrganizations(apiClient);
    },
    // Wait for Clerk auth readiness to avoid intermittent unauthenticated /organizations calls.
    enabled: isLoaded && isSignedIn,
    staleTime: ORGANIZATIONS_STALE_TIME,
  });

  // Compute ``isActive`` on the client against Clerk's current active org,
  // so the flag reflects the up-to-the-millisecond truth regardless of how
  // stale the backend response is.
  const rawOrganizations = query.data?.organizations ?? [];
  const organizations = rawOrganizations.map((org) => ({
    ...org,
    isActive: Boolean(
      clerkOrg &&
        (org.authOrganizationId === clerkOrg.id || org.id === clerkOrg.id),
    ),
  }));
  const hasActiveOrg = organizations.some((org) => org.isActive);

  return {
    organizations,
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasClerkOrgMismatch: !query.isLoading && !hasActiveOrg && Boolean(clerkOrg),
  };
}
